import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Activity,
  Command,
  Gamepad2,
  Inbox,
  LayoutDashboard,
  LayoutGrid,
  Lightbulb,
  LogIn,
  LogOut,
  MessageCircle,
  Plus,
  Settings,
  Ticket,
  TrendingUp,
  Trophy,
  User,
  UserPlus,
  Users,
  Gift,
  X,
} from "lucide-react";
import {
  logoutAppUser,
  selectAppIsAuthenticated,
} from "../redux/appAuthSlice";
import { formatBadgeCount } from "../utils/formatBadgeCount";
import { HOME_PATH, isProductEnabled } from "../config/products";

const PLAY = [
  { to: "/app/bond-predict", label: "BondPredict", Icon: TrendingUp, product: "predict" },
  { to: "/app/bond-play", label: "Play", Icon: Gamepad2, product: "play" },
];

const COMMUNITY = [
  { to: "/app/predictions", label: "Tips", Icon: Lightbulb, product: "tips" },
  { to: "/app/leaderboard", label: "Leaderboard", Icon: Trophy, product: "leaderboard" },
  { to: "/app/referrals", label: "Referrals", Icon: Gift },
];

const WORKSPACE = [
  { to: "/app/joined-bets", label: "Joined bets", Icon: Ticket, product: "house" },
  { to: "/app/requests", label: "Requests", Icon: Inbox },
  { to: "/app/bethouse/manage", label: "Operator console", Icon: Settings, product: "house" },
  { to: "/app/profile", label: "Profile", Icon: User },
];

const UnreadBadge = ({ count }) =>
  count > 0 ? (
    <span className="app-deck-badge">{formatBadgeCount(count)}</span>
  ) : null;

const DockItem = ({ to, label, Icon, end = false, badge = 0, state }) => (
  <NavLink
    to={to}
    end={end}
    state={state}
    title={label}
    className={({ isActive }) =>
      `wsdock-item ${isActive ? "wsdock-item-active" : ""}`
    }
  >
    <Icon size={20} strokeWidth={2.2} />
    {badge > 0 && <UnreadBadge count={badge} />}
    <span className="wsdock-item-label">{label}</span>
  </NavLink>
);

const DockButton = ({ label, children, className = "", ...props }) => (
  <button type="button" className={`wsdock-item ${className}`.trim()} {...props}>
    {children}
    <span className="wsdock-item-label">{label}</span>
  </button>
);

const SheetLink = ({ to, Icon, label, onClose, state }) => (
  <Link
    to={to}
    state={state}
    onClick={onClose}
    className="app-deck-sheet-link"
  >
    <Icon size={15} />
    {label}
  </Link>
);

const AppWorkspaceMobileDock = ({ onOpenPalette }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isAuthenticated = useSelector(selectAppIsAuthenticated);
  const chatUnread = useSelector((s) => s.generalChat?.unreadCount || 0);
  const activityUnread = useSelector((s) => s.activities?.unreadCount || 0);

  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const handleSignOut = () => {
    setMenuOpen(false);
    dispatch(logoutAppUser());
    navigate(HOME_PATH);
  };

  const dockUi = (
    <>
      <div className="wsdock-shell">
        <div className="wsdock-plate" aria-hidden="true" />
        <nav className="wsdock" aria-label="Mobile navigation">
          <DockItem to="/app/p2p" label="P2P" Icon={Users} end />
          <DockItem
            to="/app/leaderboard"
            label="Board"
            Icon={Trophy}
          />
          <button
            type="button"
            onClick={onOpenPalette}
            className="wsdock-fab"
            aria-label="Jump to any section"
          >
            <Command size={20} strokeWidth={2.4} />
          </button>
          <DockItem
            to="/app/general-chat"
            label="Chat"
            Icon={MessageCircle}
            badge={chatUnread}
          />
          <DockButton
            label="More"
            className={menuOpen ? "wsdock-item-active" : ""}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={20} /> : <LayoutGrid size={20} strokeWidth={2.2} />}
          </DockButton>
        </nav>
      </div>

      {menuOpen && (
        <div
          className="app-deck-sheet wsdock-sheet"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <button
            type="button"
            className="app-deck-sheet-backdrop"
            aria-label="Close"
            onClick={() => setMenuOpen(false)}
          />
          <div className="app-deck-sheet-panel">
            {PLAY.filter((item) => isProductEnabled(item.product)).length > 0 ? (
              <>
            <p className="app-deck-sheet-eyebrow">Play</p>
            <div className="app-deck-sheet-grid">
              {PLAY.filter((item) => isProductEnabled(item.product)).map(({ to, label, Icon }) => (
                <Link
                  key={to}
                  to={to}
                  className="app-deck-sheet-tile"
                  onClick={() => setMenuOpen(false)}
                >
                  <span className="app-deck-sheet-tile-icon">
                    <Icon size={18} strokeWidth={2.2} />
                  </span>
                  <span>{label}</span>
                </Link>
              ))}
            </div>
              </>
            ) : null}

            <p className="app-deck-sheet-eyebrow">Community</p>
            <div className="app-deck-sheet-grid">
              {COMMUNITY.filter((item) => isProductEnabled(item.product)).map(({ to, label, Icon }) => (
                <Link
                  key={to}
                  to={to}
                  className="app-deck-sheet-tile"
                  onClick={() => setMenuOpen(false)}
                >
                  <span className="app-deck-sheet-tile-icon">
                    <Icon size={18} strokeWidth={2.2} />
                  </span>
                  <span>{label}</span>
                </Link>
              ))}
            </div>

            <p className="app-deck-sheet-eyebrow">Pulse</p>
            <div className="app-deck-sheet-links">
              <SheetLink
                to={isAuthenticated ? "/app/activity" : "/app/login"}
                Icon={Activity}
                label={
                  activityUnread
                    ? `Bond Pulse (${formatBadgeCount(activityUnread)})`
                    : "Bond Pulse"
                }
                onClose={() => setMenuOpen(false)}
                state={
                  isAuthenticated ? undefined : { from: "/app/activity" }
                }
              />
            </div>

            <Link
              to={isAuthenticated ? "/app/p2p/create" : "/app/login"}
              state={isAuthenticated ? undefined : { from: "/app/p2p/create" }}
              className="wsdock-sheet-create"
              onClick={() => setMenuOpen(false)}
            >
              <Plus size={15} strokeWidth={2.6} />
              {isProductEnabled("house") ? "Create House" : "Create P2P"}
            </Link>

            {isAuthenticated && (
              <>
                <p className="app-deck-sheet-eyebrow">Workspace</p>
                <div className="app-deck-sheet-links">
                  {WORKSPACE.filter((item) => isProductEnabled(item.product)).map(({ to, label, Icon }) => (
                    <SheetLink
                      key={to}
                      to={to}
                      Icon={Icon}
                      label={label}
                      onClose={() => setMenuOpen(false)}
                    />
                  ))}
                </div>
                <p className="app-deck-sheet-eyebrow">Account</p>
                <div className="app-deck-sheet-links">
                  <SheetLink
                    to="/app/dashboard"
                    Icon={LayoutDashboard}
                    label="Dashboard"
                    onClose={() => setMenuOpen(false)}
                  />
                </div>
                <button
                  type="button"
                  className="app-deck-sheet-signout"
                  onClick={handleSignOut}
                >
                  <LogOut size={15} /> Sign out
                </button>
              </>
            )}

            {!isAuthenticated && (
              <div className="app-deck-sheet-auth">
                <Link
                  to="/app/login"
                  className="app-deck-sheet-auth-btn"
                  onClick={() => setMenuOpen(false)}
                >
                  <LogIn size={15} /> Log in
                </Link>
                <Link
                  to="/app/signup"
                  className="app-deck-sheet-auth-btn app-deck-sheet-auth-primary"
                  onClick={() => setMenuOpen(false)}
                >
                  <UserPlus size={15} /> Sign up
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );

  return typeof document !== "undefined"
    ? createPortal(dockUi, document.body)
    : dockUi;
};

export default AppWorkspaceMobileDock;
